 /**
  * CR307 D5: Admin Source Inspector
  * 
  * Debug panel showing the source of each configuration value:
  * - Effective value
  * - Source: overrides | template | legacy | default
  * - JSON path
  * - Actions: Reset override, Copy to override, Jump to source
  */
 
 import React, { useState, useMemo } from "react";
 import { Button } from "@/components/ui/button";
 import { Badge } from "@/components/ui/badge";
 import { ScrollArea } from "@/components/ui/scroll-area";
 import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
 import { Input } from "@/components/ui/input";
 import { 
   ChevronDown, 
   ChevronRight, 
   Trash2, 
   Copy, 
   ArrowRight,
   Search,
   Database,
   FileText,
   History,
   Box,
 } from "lucide-react";
 import { traceValueSource, type ValueSource, type ValueWithSource } from "@/lib/deal-cards/specV2/deepMerge";
 
 // ================================================================
 // TYPES
 // ================================================================
 
 interface OverrideSourceInspectorProps {
   dealTypeKey: string;
   overrides: Record<string, unknown> | null;
   template: Record<string, unknown> | null;
   legacy?: Record<string, unknown> | null;
   onResetPath?: (path: string) => void;
   onCopyToOverride?: (path: string, value: unknown) => void;
   className?: string;
 }
 
 interface InspectorEntry {
   path: string;
   key: string;
   depth: number;
   source: ValueSource;
   effectiveValue: unknown;
   overrideValue?: unknown;
   templateValue?: unknown;
   legacyValue?: unknown;
   hasChildren: boolean;
 }
 
 // ================================================================
 // HELPERS
 // ================================================================
 
 function getSourceBadgeVariant(source: ValueSource): "default" | "secondary" | "outline" | "destructive" {
   switch (source) {
     case "overrides":
       return "destructive";
     case "template":
       return "default";
     case "legacy":
       return "secondary";
     case "default":
       return "outline";
   }
 }
 
 function getSourceIcon(source: ValueSource) {
   switch (source) {
     case "overrides":
       return <Database className="h-3 w-3" />;
     case "template":
       return <FileText className="h-3 w-3" />;
     case "legacy":
       return <History className="h-3 w-3" />;
     case "default":
       return <Box className="h-3 w-3" />;
   }
 }
 
 function formatValue(value: unknown): string {
   if (value === null) return "null";
   if (value === undefined) return "undefined";
   if (typeof value === "boolean") return value ? "true" : "false";
   if (typeof value === "number") return value.toString();
   if (typeof value === "string") return `"${value.length > 30 ? value.slice(0, 30) + "..." : value}"`;
   if (Array.isArray(value)) return `[${value.length} items]`;
   if (typeof value === "object") return `{${Object.keys(value).length} keys}`;
   return String(value);
 }
 
 function flattenObject(
   obj: Record<string, unknown>,
   overrides: Record<string, unknown> | null,
   template: Record<string, unknown> | null,
   legacy: Record<string, unknown> | null,
   prefix = "",
   depth = 0
 ): InspectorEntry[] {
   const entries: InspectorEntry[] = [];
   
   for (const [key, value] of Object.entries(obj)) {
     const path = prefix ? `${prefix}.${key}` : key;
     const hasChildren = value !== null && typeof value === "object" && !Array.isArray(value) && Object.keys(value).length > 0;
     
     const traced = traceValueSource(path, overrides, template, legacy, undefined);
     
     const getNestedValue = (o: Record<string, unknown> | null, p: string): unknown => {
       if (!o) return undefined;
       const parts = p.split(".");
       let current: unknown = o;
       for (const part of parts) {
         if (current === null || current === undefined || typeof current !== "object") return undefined;
         current = (current as Record<string, unknown>)[part];
       }
       return current;
     };
     
     entries.push({
       path,
       key,
       depth,
       source: traced.source,
       effectiveValue: value,
       overrideValue: getNestedValue(overrides, path),
       templateValue: getNestedValue(template, path),
       legacyValue: getNestedValue(legacy, path),
       hasChildren,
     });
     
     if (hasChildren) {
       entries.push(...flattenObject(value as Record<string, unknown>, overrides, template, legacy, path, depth + 1));
     }
   }
   
   return entries;
 }
 
 // ================================================================
 // COMPONENT
 // ================================================================
 
 export function OverrideSourceInspector({
   dealTypeKey,
   overrides,
   template,
   legacy,
   onResetPath,
   onCopyToOverride,
   className = "",
 }: OverrideSourceInspectorProps) {
   const [searchQuery, setSearchQuery] = useState("");
   const [expandedPaths, setExpandedPaths] = useState<Set<string>>(new Set());
   
   // Build effective config for inspection
   const effectiveConfig = useMemo(() => {
     // Simple deep merge for display
     const merged: Record<string, unknown> = {};
     const sources = [legacy, template, overrides].filter(Boolean) as Record<string, unknown>[];
     for (const src of sources) {
       Object.assign(merged, src);
     }
     return merged;
   }, [overrides, template, legacy]);
   
   // Flatten for display
   const entries = useMemo(() => {
     return flattenObject(effectiveConfig, overrides, template, legacy ?? null);
   }, [effectiveConfig, overrides, template, legacy]);
   
   // Filter by search
   const filteredEntries = useMemo(() => {
     if (!searchQuery.trim()) return entries;
     const q = searchQuery.toLowerCase();
     return entries.filter(e => 
       e.path.toLowerCase().includes(q) ||
       e.key.toLowerCase().includes(q) ||
       formatValue(e.effectiveValue).toLowerCase().includes(q)
     );
   }, [entries, searchQuery]);
   
   // Group by top-level key
   const groupedEntries = useMemo(() => {
     const groups = new Map<string, InspectorEntry[]>();
     for (const entry of filteredEntries) {
       const topKey = entry.path.split(".")[0];
       if (!groups.has(topKey)) {
         groups.set(topKey, []);
       }
       groups.get(topKey)!.push(entry);
     }
     return groups;
   }, [filteredEntries]);
   
   const toggleExpand = (path: string) => {
     setExpandedPaths(prev => {
       const next = new Set(prev);
       if (next.has(path)) {
         next.delete(path);
       } else {
         next.add(path);
       }
       return next;
     });
   };
   
   return (
     <div className={`border rounded-lg bg-muted/30 ${className}`}>
       {/* Header */}
       <div className="p-3 border-b bg-muted/50 flex items-center gap-2">
         <Database className="h-4 w-4 text-muted-foreground" />
         <span className="font-medium text-sm">Source Inspector</span>
         <Badge variant="outline" className="ml-auto text-xs">
           {dealTypeKey}
         </Badge>
       </div>
       
       {/* Search */}
       <div className="p-2 border-b">
         <div className="relative">
           <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
           <Input
             placeholder="Search paths or values..."
             value={searchQuery}
             onChange={(e) => setSearchQuery(e.target.value)}
             className="pl-8 h-8 text-xs"
           />
         </div>
       </div>
       
       {/* Legend */}
       <div className="p-2 border-b flex flex-wrap gap-2 text-xs">
         <div className="flex items-center gap-1">
           <Badge variant="destructive" className="h-5 px-1.5 text-[10px]">O</Badge>
           <span className="text-muted-foreground">Overrides</span>
         </div>
         <div className="flex items-center gap-1">
           <Badge variant="default" className="h-5 px-1.5 text-[10px]">T</Badge>
           <span className="text-muted-foreground">Template</span>
         </div>
         <div className="flex items-center gap-1">
           <Badge variant="secondary" className="h-5 px-1.5 text-[10px]">L</Badge>
           <span className="text-muted-foreground">Legacy</span>
         </div>
         <div className="flex items-center gap-1">
           <Badge variant="outline" className="h-5 px-1.5 text-[10px]">D</Badge>
           <span className="text-muted-foreground">Default</span>
         </div>
       </div>
       
       {/* Entries */}
       <ScrollArea className="h-[300px]">
         <div className="p-1">
           {Array.from(groupedEntries.entries()).map(([topKey, groupEntries]) => (
             <Collapsible
               key={topKey}
               open={expandedPaths.has(topKey)}
               onOpenChange={() => toggleExpand(topKey)}
             >
               <CollapsibleTrigger className="w-full">
                 <div className="flex items-center gap-2 p-1.5 hover:bg-muted/50 rounded text-xs">
                   {expandedPaths.has(topKey) ? (
                     <ChevronDown className="h-3 w-3" />
                   ) : (
                     <ChevronRight className="h-3 w-3" />
                   )}
                   <span className="font-mono font-medium">{topKey}</span>
                   <span className="text-muted-foreground">({groupEntries.length})</span>
                 </div>
               </CollapsibleTrigger>
               <CollapsibleContent>
                 {groupEntries.map((entry) => (
                   <div
                     key={entry.path}
                     className="flex items-center gap-2 p-1.5 pl-4 hover:bg-muted/30 rounded text-xs group"
                     style={{ paddingLeft: `${entry.depth * 12 + 16}px` }}
                   >
                     {/* Path */}
                     <span className="font-mono text-muted-foreground truncate flex-1 min-w-0">
                       {entry.key}
                     </span>
                     
                     {/* Value */}
                     <span className="font-mono text-foreground truncate max-w-[120px]">
                       {formatValue(entry.effectiveValue)}
                     </span>
                     
                     {/* Source badge */}
                     <Badge 
                       variant={getSourceBadgeVariant(entry.source)}
                       className="h-5 px-1.5 text-[10px] flex items-center gap-0.5"
                     >
                       {getSourceIcon(entry.source)}
                       <span className="uppercase">{entry.source[0]}</span>
                     </Badge>
                     
                     {/* Actions (visible on hover) */}
                     <div className="opacity-0 group-hover:opacity-100 flex items-center gap-0.5">
                       {entry.source === "overrides" && onResetPath && (
                         <Button
                           variant="ghost"
                           size="icon"
                           className="h-5 w-5"
                           onClick={(e) => {
                             e.stopPropagation();
                             onResetPath(entry.path);
                           }}
                           title="Reset (remove override)"
                         >
                           <Trash2 className="h-3 w-3 text-destructive" />
                         </Button>
                       )}
                       {entry.source !== "overrides" && onCopyToOverride && (
                         <Button
                           variant="ghost"
                           size="icon"
                           className="h-5 w-5"
                           onClick={(e) => {
                             e.stopPropagation();
                             onCopyToOverride(entry.path, entry.effectiveValue);
                           }}
                           title="Copy to override"
                         >
                           <Copy className="h-3 w-3" />
                         </Button>
                       )}
                     </div>
                   </div>
                 ))}
               </CollapsibleContent>
             </Collapsible>
           ))}
           
           {filteredEntries.length === 0 && (
             <div className="p-4 text-center text-xs text-muted-foreground">
               No entries match your search.
             </div>
           )}
         </div>
       </ScrollArea>
       
       {/* Stats footer */}
       <div className="p-2 border-t bg-muted/30 flex items-center gap-4 text-xs text-muted-foreground">
         <span>
           {entries.filter(e => e.source === "overrides").length} overrides
         </span>
         <span>
           {entries.filter(e => e.source === "template").length} template
         </span>
         <span>
           {entries.filter(e => e.source === "default").length} defaults
         </span>
       </div>
     </div>
   );
 }